import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import swal from 'sweetalert';

import CRMApi from '../../../Api/CRM/CRMApi';
import GetFaqCategoryResponse from '../../../Api/CRM/Responses/FaqCategoryResponse';
import { GetFaqTranslationsResponse } from '../../../Api/CRM/Responses/FaqResponse';
import GetAllLanguagesResponse from '../../../Api/CRM/Responses/LanguagesResponse';
import Faq from '../../../Api/Models/Faq';
import TextEditor from '../../elements/formElements/TextEditor';
import handleValidationErrors from '../../helpers/ResponseErrorHelper';

type State = {
    faq: Faq;
    errors: {};
    languages: GetAllLanguagesResponse;
    faqCategories: GetFaqCategoryResponse[];
    translations: GetFaqTranslationsResponse;
};

class CreateFaq extends Component<RouteComponentProps, State> {
    constructor(props: RouteComponentProps) {
        super(props);
        this.state = {
            faq: new Faq({
                id: 0,
                language_id: 0,
                translation_id: 0,
                faq_category_id: null,
                order: 0,
                is_mobile: false,
                is_agent: false,
                question: '',
                answer: '',
            }),
            errors: {},
            languages: [],
            translations: [],
            faqCategories: [],
        };

        this.handleFieldChange = this.handleFieldChange.bind(this);
        this.handleCreateNewFaq = this.handleCreateNewFaq.bind(this);
        this.hasErrorFor = this.hasErrorFor.bind(this);
        this.renderErrorFor = this.renderErrorFor.bind(this);
    }

    componentDidMount() {
        this.loadState();
    }

    handleCreateNewFaq(event) {
        event.preventDefault();
        const { faq } = this.state;
        const { history } = this.props;

        CRMApi.faqs
            .store(faq)
            .then((createdFaq) => {
                if (createdFaq?.id) {
                    swal({
                        title: 'Success',
                        text: 'Faq created correctly',
                        icon: 'success',
                    }).then(() => {
                        history.push('/faq');
                    });
                    return;
                }

                throw new Error();
            })
            .catch((error) => {
                handleValidationErrors(error, (errors) => this.setState({ errors }));
            });
    }
    handleFieldChangeTextArea(name: string, value: string) {
        this.setState((prevState) => ({
            faq: new Faq({
                ...prevState.faq,
                [name]: value,
            }),
        }));
    }

    handleFieldChange(event) {
        if (event.target === undefined) {
            return;
        }

        const { faq } = this.state;
        const { target: input } = event;

        faq[input.name] = input.type === 'checkbox' ? input.checked : input.value;
        this.setState({ faq });
    }

    async loadState() {
        const languages = await CRMApi.languages.getAll();
        const translations = await CRMApi.faqs.getTranslations();
        const faqCategories = await CRMApi.faqCategories.getAllWithoutPagination();

        const { faq } = this.state;
        faq.language_id = languages[0].id;
        faq.translation_id = translations[0].id;

        this.setState({ languages, translations, faq, faqCategories });
    }

    hasErrorFor(field: string) {
        const { errors } = this.state;
        return !!errors[field];
    }

    renderErrorFor(field: string) {
        const { errors } = this.state;
        if (this.hasErrorFor(field)) {
            return (
                <span className="invalid-feedback">
                    <strong>{errors[field][0]}</strong>
                </span>
            );
        }

        return null;
    }

    render() {
        const { faq, languages, translations, faqCategories } = this.state;
        const languageMap = languages.reduce((map, lang) => {
            map[lang.id] = lang.name;
            return map;
        }, {});

        return (
            <div className="card">
                <div className="card-body">
                    <form onSubmit={this.handleCreateNewFaq}>
                        <div className="form-group">
                            <label htmlFor="question">
                                Question <span className="text-danger text-sm">*</span>
                            </label>
                            <input
                                id="question"
                                type="text"
                                className={`form-control ${this.hasErrorFor('question') ? 'is-invalid' : ''}`}
                                name="question"
                                value={faq.question}
                                onChange={this.handleFieldChange}
                            />
                            {this.renderErrorFor('question')}
                        </div>

                        <div className="form-group">
                            <label htmlFor="faq_category_id">Category</label>
                            <select
                                id="faq_category_id"
                                className={`form-control ${this.hasErrorFor('faq_category_id') ? 'is-invalid' : ''}`}
                                name="faq_category_id"
                                value={faq.faq_category_id}
                                onChange={this.handleFieldChange}
                            >
                                <option value="">Select a category</option>
                                {faqCategories.map((category, index) => (
                                    <option key={`${category.name} ${index + 1}`} value={category.id}>
                                        {category.name} [{languageMap[category.language_id]}]
                                    </option>
                                ))}
                            </select>
                            {this.renderErrorFor('faq_category_id')}
                        </div>

                        <div className="form-group">
                            <label htmlFor="language_id">
                                Language <span className="text-danger text-sm">*</span>
                            </label>
                            <select
                                id="language_id"
                                className={`form-control ${this.hasErrorFor('language_id') ? 'is-invalid' : ''}`}
                                name="language_id"
                                value={faq.language_id}
                                onChange={this.handleFieldChange}
                            >
                                {languages.map((lang, index) => (
                                    <option key={`${lang.name} ${index + 1}`} value={lang.id}>
                                        {lang.name}
                                    </option>
                                ))}
                            </select>
                            {this.renderErrorFor('language_id')}
                        </div>

                        <div className="form-group">
                            <label htmlFor="translation_id">
                                This is translation of <span className="text-danger text-sm">*</span>
                            </label>
                            <select
                                id="translation_id"
                                className={`form-control ${this.hasErrorFor('translation_id') ? 'is-invalid' : ''}`}
                                name="translation_id"
                                value={faq.translation_id}
                                onChange={this.handleFieldChange}
                            >
                                {translations.map((translation, index) => (
                                    <option key={`${translation.question} ${index + 1}`} value={translation.id || 0}>
                                        {translation.question}
                                    </option>
                                ))}
                            </select>
                            {this.renderErrorFor('translation_id')}
                        </div>

                        <div className="form-group">
                            <label htmlFor="answer">
                                Answer <span className="text-danger text-sm">*</span>
                            </label>
                            <TextEditor
                                id="answer"
                                value={faq.answer}
                                onEditorChange={(value) => this.handleFieldChangeTextArea('answer', value)}
                            />
                            {this.renderErrorFor('answer')}
                        </div>

                        <div className="form-group">
                            <label htmlFor="order">Order</label>
                            <input
                                id="order"
                                type="number"
                                className={`form-control ${this.hasErrorFor('order') ? 'is-invalid' : ''}`}
                                name="order"
                                value={faq.order}
                                onChange={this.handleFieldChange}
                            />
                            {this.renderErrorFor('order')}
                        </div>

                        <div className="form-group">
                            <label htmlFor="is_mobile">Is mobile</label>
                            <input
                                id="is_mobile"
                                type="checkbox"
                                className={`form-control ${this.hasErrorFor('is_mobile') ? 'is-invalid' : ''}`}
                                name="is_mobile"
                                checked={faq.is_mobile}
                                onChange={this.handleFieldChange}
                            />
                            {this.renderErrorFor('is_mobile')}
                        </div>
                        <div className="form-group">
                            <label htmlFor="is_agent">Is Agent</label>
                            <input
                                id="is_agent"
                                type="checkbox"
                                className={`form-control ${this.hasErrorFor('is_agent') ? 'is-invalid' : ''}`}
                                name="is_agent"
                                checked={faq.is_agent}
                                onChange={this.handleFieldChange}
                            />
                            {this.renderErrorFor('is_agent')}
                        </div>

                        <button type="submit" className="btn btn-primary">
                            Create
                        </button>
                    </form>
                </div>
            </div>
        );
    }
}

export default withRouter(CreateFaq);
